import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Radio, Download, ShieldCheck, Wifi, WifiOff, AlertTriangle, Trash2, Search, X, ChevronsUpDown, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Modal } from '@/components/ui/modal';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { LoadingPage } from '@/components/ui/loading';
import { useActiveRole } from '@/features/auth/hooks/use-active-role';
import { UserRole } from '@/types/api';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { cn, formatRelativeTime, useDebounce } from '@/lib/utils';
import { usePagePolling, POLL_INTERVAL_LIST_MS } from '@/lib/use-page-polling';
import {
  getGateways,
  createGateway,
  deleteGateway,
  createGatewayCertificate,
  getServerTrustCertificate,
} from '@/features/gateways/api/gateways-api';
import { getProperties } from '@/features/properties/api/properties-api';
import type { Gateway, GatewayCertificate, Property } from '@/types/api';

function getLastSeenWarning(lastSeenAt: string | null | undefined): 'never' | 'stale' | null {
  if (!lastSeenAt) return 'never';
  const diffHours = (Date.now() - new Date(lastSeenAt).getTime()) / 1000 / 3600;
  if (diffHours > 24) return 'stale';
  return null;
}

function downloadFile(filename: string, content: string) {
  const blob = new Blob([content], { type: 'application/x-pem-file' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function AdminGatewaysPage() {
  const navigate = useNavigate();
  const activeRole = useActiveRole();
  const canWrite = activeRole === UserRole.Owner || activeRole === UserRole.Admin || activeRole === UserRole.Manager;
  const canDelete = activeRole === UserRole.Owner;

  const [gateways, setGateways] = useState<Gateway[]>([]);
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState<Property[]>([]);
  const [propertyFilter, setPropertyFilter] = useState<string>('');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 300);
  const [deletingGateway, setDeletingGateway] = useState<Gateway | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCertModal, setShowCertModal] = useState(false);
  const [certData, setCertData] = useState<GatewayCertificate | null>(null);
  const [certLoading, setCertLoading] = useState(false);

  // Add form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [gatewayEui, setGatewayEui] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadGateways();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyFilter, debouncedSearch]);

  useEffect(() => {
    getProperties({ perPage: 100 })
      .then((res) => setProperties(res.items))
      .catch(() => {});
  }, []);

  // Background poll: visibility-gated list-page cadence. Silent so the
  // table doesn't flash a spinner between polls.
  usePagePolling(() => { void loadGateways({ silent: true }); }, POLL_INTERVAL_LIST_MS);

  async function loadGateways({ silent = false }: { silent?: boolean } = {}) {
    if (!silent) setLoading(true);
    try {
      const response = await getGateways({
        search: debouncedSearch.trim() || undefined,
        propertyId: propertyFilter || undefined,
        perPage: 100,
      });
      setGateways(response.items);
    } catch {
      if (!silent) setGateways([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }

  async function handleCreate() {
    if (!name.trim() || !gatewayEui.trim()) {
      setError('Name and Gateway EUI are required.');
      return;
    }
    if (!/^[0-9A-Fa-f]{16}$/.test(gatewayEui)) {
      setError('Gateway EUI must be a 16-character hex string.');
      return;
    }

    setCreating(true);
    setError('');
    try {
      const response = await createGateway({
        name: name.trim(),
        description: description.trim() || undefined,
        gatewayEui: gatewayEui.trim(),
      });
      if (response.success) {
        setShowAddModal(false);
        setName('');
        setDescription('');
        setGatewayEui('');
        await loadGateways();
      } else {
        setError(response.message || 'Failed to create gateway.');
      }
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Failed to create gateway.');
    } finally {
      setCreating(false);
    }
  }

  async function handleCreateCertificate(gatewayId: string) {
    setCertLoading(true);
    setCertData(null);
    setShowCertModal(true);
    try {
      const response = await createGatewayCertificate(gatewayId);
      if (response.success && response.data) {
        setCertData(response.data);
        await loadGateways();
      } else {
        setError(response.message || 'Failed to create certificate.');
      }
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Failed to create certificate.');
    } finally {
      setCertLoading(false);
    }
  }

  async function handleDownloadServerTrust() {
    try {
      const response = await getServerTrustCertificate();
      if (response.success && response.data) {
        downloadFile('cups.trust', response.data);
      }
    } catch {
      // silently fail
    }
  }

  async function handleDeleteConfirm() {
    if (!deletingGateway) return;
    const gw = deletingGateway;
    setDeletingGateway(null);
    try {
      await deleteGateway(gw.id);
      setGateways(prev => prev.filter(g => g.id !== gw.id));
    } catch {
      await loadGateways();
    }
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-wrap gap-3 items-center justify-between">
          <div className="flex flex-wrap gap-3 items-center">
            {canWrite && (
              <Button onClick={() => setShowAddModal(true)}>
                <Plus className="w-4 h-4" />
                Add Gateway
              </Button>
            )}
            <Button variant="outline" onClick={handleDownloadServerTrust}>
              <ShieldCheck className="w-4 h-4" />
              Download CUPS Trust Certificate
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px] max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by Gateway EUI or name..."
              className="w-full pl-8 pr-8 py-1.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-100 placeholder-gray-400 outline-none focus:ring-1 focus:ring-primary-500"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                aria-label="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <PropertyCombobox
            properties={properties}
            value={propertyFilter}
            onChange={setPropertyFilter}
          />
        </div>

        {loading ? (
          <LoadingPage />
        ) : (<>


        {gateways.length === 0 ? (
          <Card variant="elevated">
            <EmptyState
              icon={<Radio className="w-16 h-16" />}
              title={
                debouncedSearch.trim() ? 'No matches'
                : propertyFilter ? 'No gateways for this property'
                : 'No gateways configured'
              }
              description={
                debouncedSearch.trim()
                  ? `No gateways match "${debouncedSearch.trim()}". Try a different search or clear the filter.`
                  : propertyFilter
                    ? 'This property has no gateways forwarding transmissions in the last 7 days.'
                    : 'Add a LoRaWAN gateway to connect your devices, or they will be auto-registered when they first transmit.'
              }
              action={canWrite && !propertyFilter && !debouncedSearch.trim() ? { label: 'Add Gateway', onClick: () => setShowAddModal(true) } : undefined}
            />
          </Card>
        ) : (
          <Card variant="elevated" padding="none">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Gateway EUI</TableHead>
                  <TableHead>Certificate</TableHead>
                  <TableHead>Last Seen</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {gateways.map((gw) => (
                  <TableRow key={gw.id} className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50" onClick={() => navigate(`/gateways/${gw.id}`)}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {gw.status === 'Error' ? (
                          <AlertTriangle className="w-4 h-4 text-error-500" />
                        ) : getLastSeenWarning(gw.lastSeenAt) === null ? (
                          <Wifi className="w-4 h-4 text-success-500" />
                        ) : (
                          <WifiOff className="w-4 h-4 text-gray-400" />
                        )}
                        <span className="font-medium text-gray-900 dark:text-gray-100">{gw.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-sm text-gray-600 dark:text-gray-400">{gw.gatewayEui}</code>
                    </TableCell>
                    <TableCell>
                      {gw.certificateGenerated ? (
                        <Badge variant="success" size="sm">Generated</Badge>
                      ) : (
                        <Badge variant="default" size="sm">Pending</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {gw.lastSeenAt ? formatRelativeTime(gw.lastSeenAt) : 'Never'}
                        </span>
                        {getLastSeenWarning(gw.lastSeenAt) === 'never' && (
                          <Badge variant="error" size="sm">No signal</Badge>
                        )}
                        {getLastSeenWarning(gw.lastSeenAt) === 'stale' && (
                          <Badge variant="warning" size="sm">Stale</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        {!gw.certificateGenerated && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleCreateCertificate(gw.id); }}
                          >
                            <ShieldCheck className="w-4 h-4" />
                            Create CUPS Credentials
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-error-600 hover:text-error-700"
                            onClick={(e) => { e.stopPropagation(); setDeletingGateway(gw); }}
                            title="Delete gateway"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
        </>)}

        {/* Add Gateway Modal */}
        <Modal
          isOpen={showAddModal}
          onClose={() => { setShowAddModal(false); setError(''); }}
          title="Add Gateway"
          description="Register a new LoRaWAN gateway in AWS IoT Wireless."
        >
          <div className="space-y-4">
            <Input
              label="Gateway Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Building A Gateway"
              required
            />
            <Input
              label="Gateway EUI"
              value={gatewayEui}
              onChange={(e) => setGatewayEui(e.target.value)}
              placeholder="16-character hex (e.g., 0101012222223333)"
              required
            />
            <Input
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
            />
            {error && <p className="text-sm text-error-600">{error}</p>}
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => { setShowAddModal(false); setError(''); }} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleCreate} isLoading={creating} className="flex-1">
                Create Gateway
              </Button>
            </div>
          </div>
        </Modal>

        {/* Certificate Modal */}
        <Modal
          isOpen={showCertModal}
          onClose={() => { setShowCertModal(false); setCertData(null); }}
          title="Gateway CUPS Credentials"
          description="Download these files to configure your LoRaWAN gateway's CUPS connection."
          size="lg"
        >
          {certLoading ? (
            <LoadingPage />
          ) : certData ? (
            <div className="space-y-5">
              <div className="p-3 rounded-lg bg-warning-50 dark:bg-warning-500/20 border border-warning-200 dark:border-warning-500/30">
                <p className="text-sm text-warning-700 dark:text-warning-500 font-medium">
                  Save the private key now — it cannot be retrieved again after closing this dialog.
                </p>
              </div>

              <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-sm text-gray-700 dark:text-gray-300 space-y-2">
                <p className="font-medium text-gray-900 dark:text-gray-100">LoRaWAN Gateway Setup</p>
                <p>Your gateway needs three files to authenticate with AWS IoT Core for LoRaWAN via CUPS (Configuration and Update Server):</p>
                <ul className="list-disc ml-5 space-y-1">
                  <li><strong>Gateway Certificate</strong> — The gateway's identity. Upload this to your gateway's CUPS certificate field.</li>
                  <li><strong>Private Key</strong> — Proves the gateway owns the certificate. Upload to the CUPS private key field. Keep this secret.</li>
                  <li><strong>CUPS Trust Certificate</strong> — Starfield Root CA so your gateway trusts the AWS CUPS endpoint. Upload to the CUPS trust/CA field.</li>
                </ul>
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">CUPS Server URL</p>
                  <code className="text-xs font-mono select-all break-all">https://A2YJ2ON53N7KDS.cups.lorawan.us-east-1.amazonaws.com:443</code>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Gateway Certificate</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">CUPS certificate — gateway identity for mutual TLS</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => downloadFile(`${certData.certificateId}-certificate.pem.crt`, certData.certificatePem)}>
                    <Download className="w-4 h-4" />
                    Download
                  </Button>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg border border-error-200 dark:border-error-500/30 bg-error-50/50 dark:bg-error-500/10">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Private Key</p>
                    <p className="text-xs text-error-600 dark:text-error-500">CUPS private key — only available right now, keep secure</p>
                  </div>
                  <Button variant="danger" size="sm" onClick={() => downloadFile(`${certData.certificateId}-private.pem.key`, certData.privateKey)}>
                    <Download className="w-4 h-4" />
                    Download
                  </Button>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">CUPS Trust Certificate</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Starfield Root CA G2 — so the gateway trusts the AWS CUPS server</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleDownloadServerTrust}>
                    <Download className="w-4 h-4" />
                    Download
                  </Button>
                </div>
              </div>

              <div className="pt-2">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Certificate ID: <code className="font-mono">{certData.certificateId}</code>
                </p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-error-600">Failed to create certificate.</p>
          )}
        </Modal>

        <ConfirmDialog
          isOpen={!!deletingGateway}
          title={`Delete gateway "${deletingGateway?.name}"?`}
          description={
            <>
              <p>This will permanently disconnect the gateway from the AWS IoT backend.</p>
              <p>If the gateway is re-added later, it may need to be reprovisioned before it can reconnect.</p>
            </>
          }
          confirmLabel="Delete Gateway"
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeletingGateway(null)}
        />
      </div>
    </div>
  );
}

interface PropertyComboboxProps {
  properties: Property[];
  value: string;
  onChange: (value: string) => void;
}

function PropertyCombobox({ properties, value, onChange }: PropertyComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = properties.find((p) => p.id === value);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return properties;
    return properties.filter((p) => p.name.toLowerCase().includes(q));
  }, [properties, search]);

  useEffect(() => {
    function handleMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  function handleSelect(id: string) {
    onChange(id);
    setOpen(false);
    setSearch('');
  }

  return (
    <div ref={containerRef} className="relative min-w-[220px]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-100 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
      >
        <span className={cn('truncate', !selected && 'text-gray-500 dark:text-gray-400')}>
          {selected ? selected.name : 'All properties'}
        </span>
        <ChevronsUpDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden">
          <div className="p-2 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search properties..."
                className="w-full pl-7 pr-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 rounded text-gray-900 dark:text-gray-100 placeholder-gray-400 outline-none"
              />
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto py-1">
            <button
              type="button"
              onClick={() => handleSelect('')}
              className={cn(
                'w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors',
                value === '' ? 'font-semibold text-primary-600 dark:text-primary-400' : 'text-gray-700 dark:text-gray-300'
              )}
            >
              <span>All properties</span>
              {value === '' && <Check className="w-3.5 h-3.5 flex-shrink-0 ml-2" />}
            </button>
            {filtered.length === 0 ? (
              <p className="text-xs text-gray-500 px-3 py-2">No results</p>
            ) : (
              filtered.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => handleSelect(p.id)}
                  className={cn(
                    'w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors',
                    p.id === value ? 'font-semibold text-primary-600 dark:text-primary-400' : 'text-gray-700 dark:text-gray-300'
                  )}
                >
                  <span className="truncate">{p.name}</span>
                  {p.id === value && <Check className="w-3.5 h-3.5 flex-shrink-0 ml-2" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
